import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Shared RAF timeline for the 60-second demo reels.
 * Keyboard: Space play/pause · ← / → previous/next act · R restart.
 */
export const useReelTimeline = (acts, totalSeconds) => {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [ended, setEnded] = useState(false);
  const rafRef = useRef();
  const lastTsRef = useRef();

  useEffect(() => {
    if (!playing || ended) return;
    const tick = (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;
      setElapsed((e) => {
        const next = e + dt;
        if (next >= totalSeconds) {
          setEnded(true);
          setPlaying(false);
          return totalSeconds;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lastTsRef.current = null;
    };
  }, [playing, ended, totalSeconds]);

  const currentAct = useMemo(() => {
    for (let i = acts.length - 1; i >= 0; i--) {
      if (elapsed >= acts[i].startAt) return acts[i];
    }
    return acts[0];
  }, [acts, elapsed]);

  const seek = useCallback((t) => {
    lastTsRef.current = null;
    setElapsed(Math.max(0, Math.min(totalSeconds, t)));
    setEnded(false);
  }, [totalSeconds]);

  const restart = useCallback(() => {
    seek(0);
    setPlaying(true);
  }, [seek]);

  const toggle = useCallback(() => {
    if (ended) restart();
    else setPlaying((p) => !p);
  }, [ended, restart]);

  const nextAct = useCallback(() => {
    const next = acts.find((a) => a.startAt > elapsed + 0.05);
    if (next) seek(next.startAt);
    else { seek(totalSeconds); setEnded(true); setPlaying(false); }
  }, [acts, elapsed, seek, totalSeconds]);

  const prevAct = useCallback(() => {
    const idx = acts.indexOf(currentAct);
    const backToStart = elapsed - currentAct.startAt > 1 || idx === 0;
    seek(backToStart ? currentAct.startAt : acts[idx - 1].startAt);
  }, [acts, currentAct, elapsed, seek]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
      if (e.key === " " || e.code === "Space") { e.preventDefault(); toggle(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); nextAct(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prevAct(); }
      else if (e.key === "r" || e.key === "R") restart();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle, nextAct, prevAct, restart]);

  const actProgress = Math.min(1, (elapsed - currentAct.startAt) / currentAct.duration);

  return { elapsed, playing, ended, currentAct, actProgress, toggle, restart, seek, nextAct, prevAct };
};
